import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";

import { ERR_INTERNET_DISCONNECTED } from "utils/ErrorCodes";
import { Message } from "utils/feedback/Message";
import { DeleteEvent, Event, EventGroup, MoveEvent } from "utils/history/Event";
import History from "utils/history/History";
import { ListInfo } from "utils/list/List";
import TableActionbar from "utils/list/TableActionbar";

import BlueprintLibraryInfo from "core/blueprint/base/BlueprintLibraryInfo";
import {
	BlueprintInfo,
	getBlueprintInfo,
	Right
} from "core/blueprint/base/BlueprintService";
import {
	CompetenceList,
	getCompetenceListContent,
	updateCompetenceListContent
} from "core/blueprint/competencelist/CompetenceListService";
import EditActionbar from "core/blueprint/competencelist/EditActionbar";
import { collectLabels } from "core/blueprint/competencelist/Labels";
import Prompt from "lib/components/prompt/Prompt";
import CompetenceTable from "./CompetenceTable";

const CompetenceListView: React.FC = () => {
	const COMPETENCE_LIST = "competence_list";

	const navigate = useNavigate();
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();

	const [info, setInfo] = useState<BlueprintInfo>();
	const [content, setContent] = useState<CompetenceList>({ competences: [] });
	const [listInfo, setListInfo] = useState<ListInfo<string>>(ListInfo.init());
	const [actionHistory, setActionHistory] = useState(new History(content));
	const [message, setMessage] = useState<Message>();

	const labels = useMemo(
		() => collectLabels(content.competences),
		[content]
	);

	useEffect(loadCompetenceList, [id]);

	useEffect(() => {
		setListInfo(prevInfo =>
			prevInfo.withKeys(content.competences.map(field => field.uuid))
		);
	}, [content]);

	function loadCompetenceList() {
		if (!id) {
			return;
		}

		const idValue = Number.parseInt(id);
		if (Number.isNaN(idValue)) {
			setMessage({
				severity: "error",
				text: t("invalidDocumentId", { document: COMPETENCE_LIST })
			});
			return;
		}

		Promise.all([
			getBlueprintInfo(idValue).then(data => setInfo(data)),
			getCompetenceListContent(idValue).then(data => {
				setContent(data);
				setActionHistory(new History(data));
			})
		]).catch(onLoadFailure);
	}

	function undo(): void {
		console.assert(actionHistory.hasPrevious());
		if (actionHistory.hasPrevious()) {
			setContent(actionHistory.undo());
		}
	}

	function redo(): void {
		console.assert(actionHistory.hasNext());
		if (actionHistory.hasNext()) {
			setContent(actionHistory.redo());
		}
	}

	function onSaveSuccess(): void {
		setMessage({
			severity: "success",
			text: t("saveDocumentSuccess", { document: COMPETENCE_LIST })
		});
		loadCompetenceList();
	}

	function onLoadFailure(): void {
		setMessage({
			severity: "error",
			text: t("loadDocumentError", { document: COMPETENCE_LIST })
		});
	}

	function onSaveFailure(reason: Error): void {
		let errorMessage: string;
		if (reason.message === ERR_INTERNET_DISCONNECTED) {
			errorMessage = t("internetDisconnectedError");
		} else {
			errorMessage = t("saveDocumentFailure", {
				document: COMPETENCE_LIST
			});
		}

		setMessage({
			severity: "error",
			text: errorMessage
		});
	}

	function save(): Promise<void> {
		console.assert(actionHistory !== undefined);
		setMessage(undefined);

		if (!actionHistory.hasPrevious() || !id) {
			return Promise.resolve();
		}

		const actions = actionHistory.getActions();
		const objects = actions.flatMap(action => action.patches());

		const idValue = Number.parseInt(id);
		return updateCompetenceListContent(idValue, objects)
			.then(onSaveSuccess)
			.catch(onSaveFailure);
	}

	function close(): void {
		navigate("/blueprints");
	}

	function updateField(event: Event): void {
		console.assert(actionHistory !== undefined);

		event.applyContext("/competences");
		const action = event.toAction();
		setContent(actionHistory.apply(action));
	}

	function selectFields(keys: string[], select: boolean): void {
		setListInfo(listInfo.markSelected(keys, select));
	}

	function handleMoveUp(): void {
		const events: Event[] = [];
		for (const [index, field] of content.competences.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.push(
					new MoveEvent(
						"/competences/" + index,
						"/competences/" + (index - 1)
					)
				);
			}
		}
		const event = new EventGroup(events);
		setContent(actionHistory.apply(event.toAction()));
	}

	function handleMoveDown(): void {
		const events: Event[] = [];
		for (const [index, field] of content.competences.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(
					new MoveEvent(
						"/competences/" + index,
						"/competences/" + (index + 1)
					)
				);
			}
		}
		const event = new EventGroup(events);
		setContent(actionHistory.apply(event.toAction()));
	}

	function handleDelete(): void {
		const events: Event[] = [];
		for (const [index, field] of content.competences.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(new DeleteEvent("/competences/" + index, field));
			}
		}
		const event = new EventGroup(events);
		setContent(actionHistory.apply(event.toAction()));
	}

	return (
		<>
			{message && (
				<Alert severity={message.severity}>{message.text}</Alert>
			)}
			<Typography id="title" variant="h1">
				{info?.name}
			</Typography>

			{info &&
				info.sourceBlueprintId !== null &&
				info.sourceBlueprintVersion !== null && (
					<BlueprintLibraryInfo
						id={info.id}
						enabled={info.rights.includes(Right.ADMIN) ?? false}
						blueprintId={info.sourceBlueprintId}
						blueprintVersion={info.sourceBlueprintVersion}
						onUpdate={loadCompetenceList}
					/>
				)}

			<EditActionbar
				undoEnabled={actionHistory.hasPrevious()}
				redoEnabled={actionHistory.hasNext()}
				saveEnabled={actionHistory.hasPrevious()}
				onUndo={undo}
				onRedo={redo}
				onSave={save}
				onClose={close}
			/>

			<TableActionbar
				listInfo={listInfo}
				onMoveUp={handleMoveUp}
				onMoveDown={handleMoveDown}
				onDelete={handleDelete}
			/>
			<CompetenceTable
				elements={content.competences}
				labels={labels}
				listInfo={listInfo}
				editable={info?.rights.includes(Right.WRITE) ?? false}
				onSelect={selectFields}
				onChange={updateField}
				onInsert={updateField}
			/>

			<Prompt
				when={actionHistory.hasPrevious()}
				message={t("exitDocumentMessage")}
			/>
		</>
	);
};

export default CompetenceListView;
